import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { axios, useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function MesajGunluguPage() {
  const { user } = useAuth();

  // Kuyruk Durumu State'leri
  const [queueState, setQueueState] = useState({
    status: 'idle',
    isProcessing: false,
    isPaused: false,
    isWaitingCooldown: false,
    cooldownRemainingSeconds: 0,
    cooldownTotalSeconds: 240,
    batchLimit: 100,
    sentInCurrentBatch: 0,
    messageIntervalSeconds: 4,
    queueLength: 0,
    currentJob: null,
    pendingList: [],
    lastSentAt: null,
  });

  const [statsToday, setStatsToday] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    cancelled: 0,
  });

  // Log Listesi & Öğretmen Listesi
  const [logs, setLogs] = useState([]);
  const [sendersList, setSendersList] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  // Günlük Seçim & Filtre State'leri
  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [filterSenderId, setFilterSenderId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Seçilen Grup Detay Modalı State'leri
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupFilterStatus, setGroupFilterStatus] = useState('all');

  // Tekil Mesaj Detay veya Tümünü İptal Modalleri
  const [showCancelAllModal, setShowCancelAllModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Canlı Takip Timer
  const [autoRefresh, setAutoRefresh] = useState(true);
  const timerRef = useRef(null);

  // Tarih Değiştirme Yardımcıları
  const handlePrevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleToday = () => {
    setSelectedDate(todayStr);
  };

  // Kuyruk Durumunu Çek
  const fetchQueueStatus = useCallback(async () => {
    try {
      const res = await axios.get('/whatsapp/queue-status');
      if (res.data && res.data.success) {
        setQueueState(res.data.queue || {});
        setStatsToday(res.data.statsToday || { total: 0, sent: 0, failed: 0, pending: 0, cancelled: 0 });
      }
    } catch (err) {
      console.error('Kuyruk durumu hatası:', err);
    }
  }, []);

  // Logları Çek (Seçili Günün tüm loglarını gruplamak için limit 1000)
  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = {
        page: 1,
        limit: 1000,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        source_module: filterModule !== 'all' ? filterModule : undefined,
        sender_id: filterSenderId !== 'all' ? filterSenderId : undefined,
        search: searchQuery.trim() || undefined,
        date_start: selectedDate || undefined,
        date_end: selectedDate || undefined,
      };

      const res = await axios.get('/whatsapp/logs', { params });
      if (res.data && res.data.success) {
        const fetchedLogs = res.data.data || [];
        setLogs(fetchedLogs);
        setTotalRecords(res.data.pagination?.total || fetchedLogs.length);
        if (res.data.senders && res.data.senders.length > 0) {
          setSendersList(res.data.senders);
        }

        // Eğer açık bir grup modalı varsa verisini güncelle
        setSelectedGroup((prevGroup) => {
          if (!prevGroup) return null;
          const updatedItems = fetchedLogs.filter((item) => {
            const tKey = item.sender_name || 'Sistem / Yönetim';
            const mKey = item.source_module || item.type || 'genel';
            return `${tKey}__${mKey}` === prevGroup.key;
          });

          let sent = 0, failed = 0, pending = 0, cancelled = 0;
          for (const it of updatedItems) {
            if (it.status === 'sent') sent++;
            else if (it.status === 'failed') failed++;
            else if (it.status === 'pending') pending++;
            else if (it.status === 'cancelled') cancelled++;
          }

          return {
            ...prevGroup,
            total: updatedItems.length,
            sent,
            failed,
            pending,
            cancelled,
            items: updatedItems,
          };
        });
      }
    } catch (err) {
      console.error('Mesaj logları yüklenirken hata:', err);
    } finally {
      setLoadingLogs(false);
    }
  }, [filterStatus, filterModule, filterSenderId, searchQuery, selectedDate]);

  useEffect(() => {
    fetchQueueStatus();
    fetchLogs();
  }, [fetchQueueStatus, fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      fetchQueueStatus();
      if (queueState.queueLength > 0 || queueState.isProcessing) {
        fetchLogs();
      }
    }, 3000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, fetchQueueStatus, fetchLogs, queueState.queueLength, queueState.isProcessing]);

  // Kuyruğu Duraklat
  const handlePauseQueue = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post('/whatsapp/queue/pause');
      if (res.data && res.data.success) {
        toast.success(res.data.message || 'Kuyruk duraklatıldı.');
        fetchQueueStatus();
      }
    } catch (err) {
      toast.error('Kuyruk duraklatılamadı.');
    } finally {
      setActionLoading(false);
    }
  };

  // Kuyruğu Devam Ettir
  const handleResumeQueue = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post('/whatsapp/queue/resume');
      if (res.data && res.data.success) {
        toast.success(res.data.message || 'Kuyruk devam ettiriliyor.');
        fetchQueueStatus();
      }
    } catch (err) {
      toast.error('Kuyruk devam ettirilemedi.');
    } finally {
      setActionLoading(false);
    }
  };

  // Tekil Mesajı İptal Et
  const handleCancelMessage = async (idOrLogId) => {
    try {
      const res = await axios.post(`/whatsapp/queue/cancel/${idOrLogId}`);
      if (res.data && res.data.success) {
        toast.success(res.data.message || 'Mesaj iptal edildi.');
        fetchQueueStatus();
        fetchLogs();
      } else {
        toast.error(res.data?.message || 'İptal edilemedi.');
      }
    } catch (err) {
      toast.error('İşlem sırasında hata oluştu.');
    }
  };

  // Tüm Kuyruğu İptal Et
  const handleCancelAll = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post('/whatsapp/queue/cancel-all');
      if (res.data && res.data.success) {
        toast.success(res.data.message || 'Kuyruk temizlendi.');
        setShowCancelAllModal(false);
        fetchQueueStatus();
        fetchLogs();
      }
    } catch (err) {
      toast.error('Tüm kuyruk iptal edilemedi.');
    } finally {
      setActionLoading(false);
    }
  };

  // Mesajı Tekrar Gönder
  const handleRetryMessage = async (logId) => {
    try {
      const res = await axios.post(`/whatsapp/retry/${logId}`);
      if (res.data && res.data.success) {
        toast.success('Mesaj kuyruğa alındı, sırayla gönderilecek.');
        fetchQueueStatus();
        fetchLogs();
      }
    } catch (err) {
      toast.error('Yeniden gönderme başarısız: ' + (err.response?.data?.message || err.message));
    }
  };

  // Kalan mola süresi formatlayıcı (mm:ss)
  const formatCooldown = (seconds) => {
    if (!seconds || seconds <= 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Modül Rozeti
  const getModuleBadge = (moduleName) => {
    const map = {
      yoklama: { label: '📋 Yoklama', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
      rehberlik: { label: '🎯 Rehberlik', bg: '#F5F3FF', color: '#6D28D9', border: '#DDD6FE' },
      rehberlik_seans_olusturma: { label: '📅 Rehberlik Seans', bg: '#F5F3FF', color: '#6D28D9', border: '#DDD6FE' },
      toplu_mesaj: { label: '📢 Toplu Mesaj', bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
      etut: { label: '📚 Etüt', bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
      deneme: { label: '📊 Deneme', bg: '#EEF2FF', color: '#4338CA', border: '#C7D2FE' },
      deneme_sonuc: { label: '📊 Deneme Sonuç', bg: '#EEF2FF', color: '#4338CA', border: '#C7D2FE' },
      muhasebe: { label: '💰 Muhasebe', bg: '#FDF2F8', color: '#BE185D', border: '#FBCFE8' },
      odev: { label: '📝 Ödev', bg: '#F0F9FF', color: '#0369A1', border: '#BAE6FD' },
      odev_kontrol: { label: '📝 Ödev Kontrol', bg: '#F0F9FF', color: '#0369A1', border: '#BAE6FD' },
      kullanici_hesap: { label: '🔑 Hesap Bilgisi', bg: '#FEFCE8', color: '#A16207', border: '#FEF08A' },
      hesap_bilgileri: { label: '🔑 Hesap Bilgisi', bg: '#FEFCE8', color: '#A16207', border: '#FEF08A' },
    };

    const item = map[moduleName] || {
      label: moduleName ? `📌 ${moduleName}` : '💬 Genel',
      bg: '#F8FAFC',
      color: '#334155',
      border: '#E2E8F0',
    };

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 8px',
          borderRadius: 6,
          fontSize: '0.74rem',
          fontWeight: 800,
          background: item.bg,
          color: item.color,
          border: `1px solid ${item.border}`,
          whiteSpace: 'nowrap',
        }}
      >
        {item.label}
      </span>
    );
  };

  // Durum Rozeti
  const getStatusBadge = (status) => {
    if (status === 'sent') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: '0.78rem',
            fontWeight: 800,
            background: '#ECFDF5',
            color: '#047857',
            border: '1px solid #A7F3D0',
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10B981' }} />
          İletildi
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: '0.78rem',
            fontWeight: 800,
            background: '#FEF2F2',
            color: '#DC2626',
            border: '1px solid #FECACA',
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#EF4444' }} />
          Başarısız
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: '0.78rem',
            fontWeight: 800,
            background: '#EFF6FF',
            color: '#1D4ED8',
            border: '1px solid #BFDBFE',
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563EB' }} />
          Kuyrukta
        </span>
      );
    }
    if (status === 'cancelled') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: '0.78rem',
            fontWeight: 800,
            background: '#F1F5F9',
            color: '#64748B',
            border: '1px solid #CBD5E1',
          }}
        >
          🚫 İptal
        </span>
      );
    }
    return <span style={{ color: '#0F172A', fontWeight: 700 }}>{status}</span>;
  };

  // Canlı Kuyruk İlerleme Hesaplaması (Toplam Kaç Mesaj Gidiyorsa / Giden Mesaj Sayısı)
  const liveSent = queueState.sessionSent || 0;
  const liveTotal = Math.max(1, queueState.sessionTotal || (liveSent + (queueState.queueLength || 0)));
  const livePercent = queueState.progressPercent != null 
    ? queueState.progressPercent 
    : Math.min(100, Math.round((liveSent / liveTotal) * 100));

  // Seçili Günün Hoca & İşlem Bazında Gruplanmış Barları
  const groupedSummaryBars = useMemo(() => {
    if (!logs || logs.length === 0) return [];

    const map = new Map();
    for (const item of logs) {
      const teacherKey = item.sender_name || 'Sistem / Yönetim';
      const moduleKey = item.source_module || item.type || 'genel';
      const groupKey = `${teacherKey}__${moduleKey}`;

      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          sender_name: teacherKey,
          sender_role: item.sender_role || 'ogretmen',
          source_module: moduleKey,
          total: 0,
          sent: 0,
          failed: 0,
          pending: 0,
          cancelled: 0,
          latestTime: item.created_at,
          items: [],
        });
      }

      const group = map.get(groupKey);
      group.total += 1;
      if (item.status === 'sent') group.sent += 1;
      else if (item.status === 'failed') group.failed += 1;
      else if (item.status === 'pending') group.pending += 1;
      else if (item.status === 'cancelled') group.cancelled += 1;
      group.items.push(item);
    }

    return Array.from(map.values()).sort((a, b) => new Date(b.latestTime) - new Date(a.latestTime));
  }, [logs]);

  // Modal İçerisindeki Filtrelenmiş Mesajlar
  const modalFilteredItems = useMemo(() => {
    if (!selectedGroup || !selectedGroup.items) return [];
    return selectedGroup.items.filter((item) => {
      if (groupFilterStatus !== 'all' && item.status !== groupFilterStatus) {
        return false;
      }
      if (groupSearchQuery.trim()) {
        const q = groupSearchQuery.toLowerCase().trim();
        const rName = (item.recipient_name || '').toLowerCase();
        const phone = (item.phone || item.to_phone || '').toLowerCase();
        const msg = (item.message || '').toLowerCase();
        if (!rName.includes(q) && !phone.includes(q) && !msg.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [selectedGroup, groupFilterStatus, groupSearchQuery]);

  // Kuyruk Aktif mi?
  const isQueueActive = queueState.queueLength > 0 || queueState.isProcessing || queueState.isWaitingCooldown;

  return (
    <div className="mesaj-gunlugu-container">
      {/* Mobil Uyumlu ve Temiz Kenarlıklı CSS */}
      <style>{`
        .mesaj-gunlugu-container {
          display: flex;
          flex-direction: column;
          gap: 14px;
          padding: 6px 4px 40px 4px;
          position: relative;
          color: #0F172A;
          width: 100%;
          box-sizing: border-box;
        }

        .mg-card {
          background: #FFFFFF;
          border-radius: 14px;
          padding: 16px 18px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          border: 1px solid #E2E8F0;
          box-sizing: border-box;
          width: 100%;
        }

        .mg-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .mg-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mg-date-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .mg-date-controls {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .mg-filter-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .mg-input,
        .mg-select {
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid #CBD5E1;
          background: #FFFFFF;
          font-size: 0.84rem;
          font-weight: 700;
          color: #0F172A;
          box-sizing: border-box;
          outline: none;
          transition: border-color 0.2s;
        }

        .mg-input:focus,
        .mg-select:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }

        .mg-group-card {
          background: #FFFFFF;
          border: 1px solid #E2E8F0;
          border-radius: 12px;
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .mg-group-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(37, 99, 235, 0.08);
          border-color: #93C5FD;
        }

        .mg-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          backdrop-filter: blur(4px);
          z-index: 99999;
          display: flex;
          justify-content: flex-end;
          align-items: stretch;
        }

        .mg-modal-drawer {
          width: 100%;
          max-width: 680px;
          height: 100vh;
          background: #FFFFFF;
          box-shadow: -10px 0 40px rgba(0,0,0,0.25);
          display: flex;
          flex-direction: column;
          animation: slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          box-sizing: border-box;
        }

        .mg-detail-item {
          background: #FFFFFF;
          border-radius: 10px;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        @media (max-width: 768px) {
          .mesaj-gunlugu-container {
            padding: 4px 2px 30px 2px;
            gap: 12px;
          }

          .mg-card {
            padding: 12px 14px;
            border-radius: 12px;
          }

          .mg-header {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }

          .mg-header-actions {
            display: grid;
            grid-template-columns: 1fr 1fr;
            width: 100%;
            gap: 8px;
          }

          .mg-header-actions button {
            justify-content: center;
            width: 100%;
          }

          .mg-date-bar {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .mg-date-controls {
            display: grid;
            grid-template-columns: 1fr 1.3fr 1fr;
            gap: 6px;
            width: 100%;
          }

          .mg-date-controls button,
          .mg-date-controls input {
            width: 100% !important;
            min-width: 0;
            text-align: center;
          }

          .mg-today-btn {
            width: 100%;
            margin-top: 4px;
          }

          .mg-filter-row {
            flex-direction: column;
            width: 100%;
            gap: 8px;
          }

          .mg-filter-row input,
          .mg-filter-row select {
            width: 100% !important;
          }

          .mg-modal-drawer {
            max-width: 100% !important;
            height: 100% !important;
          }
        }
      `}</style>

      {/* ── ÜST BAŞLIK VE CANLI KONTROL ŞERİDİ ── */}
      <div className="mg-card">
        <div className="mg-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.5rem' }}>📨</span>
              <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 900, color: '#0F172A' }}>
                WhatsApp Mesaj Gönderim & Kuyruk Takibi
              </h1>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#2563EB', fontWeight: 600 }}>
              {queueState.messageIntervalSeconds ?? 4} saniyede bir 1 mesaj gönderilir • Her {queueState.batchLimit ?? 100} mesajda bir {Math.round((queueState.cooldownTotalSeconds ?? 240) / 60)} dakika güvenlik molası verilir.
            </p>
          </div>

          {/* Canlı Takip & Yenile Butonları */}
          <div className="mg-header-actions">
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.8rem',
                padding: '8px 14px',
                borderRadius: 8,
                fontWeight: 800,
                background: autoRefresh ? '#10B981' : '#FFFFFF',
                border: autoRefresh ? '1px solid #10B981' : '1px solid #CBD5E1',
                color: autoRefresh ? '#FFFFFF' : '#0F172A',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: autoRefresh ? '#FFFFFF' : '#EF4444',
                }}
              />
              {autoRefresh ? 'Canlı Takip Açık' : 'Canlı Takip Kapalı'}
            </button>

            <button
              onClick={() => {
                fetchQueueStatus();
                fetchLogs();
                toast.success('Yenilendi');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.8rem',
                padding: '8px 14px',
                borderRadius: 8,
                fontWeight: 800,
                background: '#FFFFFF',
                border: '1px solid #2563EB',
                color: '#2563EB',
                cursor: 'pointer',
              }}
            >
              🔄 Yenile
            </button>
          </div>
        </div>
      </div>

      {/* ── EN ÜST: ŞU ANDA DEVAM EDEN KUYRUKTA MESAJ DURUMU ── */}
      {!isQueueActive ? (
        /* KUYRUKTA AKTİF MESAJ YOKSA: Temiz Durum Şeridi */
        <div
          className="mg-card"
          style={{
            background: '#F0FDF4',
            borderColor: '#BBF7D0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981' }} />
            <span style={{ fontSize: '0.92rem', fontWeight: 800, color: '#047857' }}>
              Şu anda aktif gönderilen mesaj yok
            </span>
            <span style={{ fontSize: '0.8rem', color: '#2563EB', fontWeight: 600 }}>
              (Sistem yeni mesaj gönderimleri için hazır)
            </span>
          </div>

          <div style={{ fontSize: '0.82rem', color: '#0F172A', fontWeight: 700 }}>
            Bugün: <strong style={{ color: '#047857' }}>{statsToday.sent} Başarılı</strong> /{' '}
            <strong style={{ color: '#DC2626' }}>{statsToday.failed} Hatalı</strong>
          </div>
        </div>
      ) : (
        /* KUYRUKTA AKTİF MESAJ VARSA: Canlı İlerleme Çubuğu ve Durdur / Devam / İptal Kontrolleri */
        <div className="mg-card" style={{ borderLeft: '4px solid #2563EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            {/* Sol: Canlı Durum */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 900, color: '#0F172A' }}>
                🚀 AKTİF GÖNDERİM KUYRUĞU:
              </div>

              {queueState.isPaused ? (
                <span style={{ padding: '4px 10px', borderRadius: 6, background: '#FEF3C7', color: '#B45309', fontWeight: 800, fontSize: '0.78rem' }}>
                  ⏸️ Duraklatıldı
                </span>
              ) : queueState.isWaitingCooldown ? (
                <span style={{ padding: '4px 10px', borderRadius: 6, background: '#FEF2F2', color: '#DC2626', fontWeight: 800, fontSize: '0.78rem' }}>
                  ⏳ {Math.round((queueState.cooldownTotalSeconds ?? 240) / 60)} Dk Güvenlik Molası (Kalan: {formatCooldown(queueState.cooldownRemainingSeconds)})
                </span>
              ) : (
                <span style={{ padding: '4px 10px', borderRadius: 6, background: '#ECFDF5', color: '#047857', fontWeight: 800, fontSize: '0.78rem' }}>
                  🟢 Mesajlar İletiliyor ({queueState.messageIntervalSeconds ?? 4} sn / adet)
                </span>
              )}
            </div>

            {/* Sağ: Kaçta Kaçı Gidecek Bilgisi (Giden / Toplam) & Kontroller */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#2563EB' }}>
                  {liveSent} / {liveTotal}
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#047857', marginLeft: 6 }}>
                    İletildi (%{livePercent})
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#0F172A', fontWeight: 700 }}>
                  Kuyrukta: <strong style={{ color: '#DC2626' }}>{queueState.queueLength} mesaj sırada</strong>
                </div>
              </div>

              {/* Kontrol Butonları */}
              <div style={{ display: 'flex', gap: 6 }}>
                {queueState.isPaused ? (
                  <button
                    onClick={handleResumeQueue}
                    disabled={actionLoading}
                    style={{ background: '#10B981', color: '#FFFFFF', padding: '7px 14px', fontSize: '0.8rem', borderRadius: 8, fontWeight: 800, border: 'none', cursor: 'pointer' }}
                  >
                    ▶️ Devam
                  </button>
                ) : (
                  <button
                    onClick={handlePauseQueue}
                    disabled={actionLoading}
                    style={{ background: '#F59E0B', color: '#000000', padding: '7px 14px', fontSize: '0.8rem', borderRadius: 8, fontWeight: 800, border: 'none', cursor: 'pointer' }}
                  >
                    ⏸️ Durdur
                  </button>
                )}

                <button
                  onClick={() => setShowCancelAllModal(true)}
                  disabled={actionLoading}
                  style={{ background: '#EF4444', color: '#FFFFFF', padding: '7px 14px', fontSize: '0.8rem', borderRadius: 8, fontWeight: 800, border: 'none', cursor: 'pointer' }}
                >
                  ✕ İptal Et
                </button>
              </div>
            </div>
          </div>

          {/* Dolan Canlı İlerleme Çubuğu */}
          <div
            style={{
              width: '100%',
              height: 14,
              background: '#F1F5F9',
              borderRadius: 99,
              overflow: 'hidden',
              position: 'relative',
              border: '1px solid #E2E8F0',
            }}
          >
            <div
              style={{
                width: `${livePercent}%`,
                height: '100%',
                background:
                  livePercent >= 90
                    ? 'linear-gradient(90deg, #10B981 0%, #059669 100%)'
                    : 'linear-gradient(90deg, #2563EB 0%, #10B981 100%)',
                borderRadius: 99,
                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>

          {/* Açıklama */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, fontSize: '0.78rem', fontWeight: 700, flexWrap: 'wrap', gap: 6 }}>
            <span>
              {queueState.isWaitingCooldown ? (
                <strong style={{ color: '#DC2626' }}>⏳ {queueState.batchLimit ?? 100} mesaj partisi doldu, {Math.round((queueState.cooldownTotalSeconds ?? 240) / 60)} dakika güvenlik molası veriliyor (Kalan: {formatCooldown(queueState.cooldownRemainingSeconds)}).</strong>
              ) : (
                <span style={{ color: '#0F172A' }}>
                  Toplam <strong style={{ color: '#2563EB' }}>{liveTotal} mesajdan {liveSent} tanesi iletildi.</strong>
                  {queueState.queueLength > 0 && (
                    <span style={{ color: '#64748B', marginLeft: 6 }}>
                      (Sonraki {Math.round((queueState.cooldownTotalSeconds ?? 240) / 60)} dk molaya: <strong style={{ color: '#DC2626' }}>{Math.max(0, (queueState.batchLimit ?? 100) - (queueState.sentInCurrentBatch || 0))} mesaj</strong>)
                    </span>
                  )}
                </span>
              )}
            </span>
            <span style={{ color: '#2563EB' }}>
              İlerleme Hızı: <strong>{queueState.messageIntervalSeconds ?? 4} saniye / mesaj</strong>
            </span>
          </div>

          {/* Kuyruktaki Mesajların Tek Tek Sıralı Listesi */}
          {queueState.pendingList && queueState.pendingList.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 900, color: '#2563EB' }}>
                SIRADAKİ MESAJLAR ({queueState.pendingList.length} adet):
              </div>
              {queueState.pendingList.map((item, idx) => (
                <div
                  key={item.id || item.logId || idx}
                  style={{
                    background: idx === 0 && !queueState.isPaused && !queueState.isWaitingCooldown ? '#EFF6FF' : '#F8FAFC',
                    border: '1px solid #CBD5E1',
                    borderRadius: 8,
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 900, background: '#2563EB', color: '#FFFFFF' }}>
                      #{idx + 1}
                    </span>
                    <span style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0F172A' }}>{item.recipientName}</span>
                    <span style={{ fontSize: '0.78rem', color: '#2563EB', fontWeight: 700 }}>({item.toPhone})</span>
                    {getModuleBadge(item.sourceModule)}
                    <span style={{ fontSize: '0.75rem', color: '#DC2626', fontWeight: 700 }}>Hoca: {item.senderName}</span>
                  </div>

                  <button
                    onClick={() => handleCancelMessage(item.id || item.logId)}
                    style={{ padding: '4px 10px', fontSize: '0.72rem', background: '#FEF2F2', color: '#DC2626', border: '1px solid #CBD5E1', borderRadius: 6, fontWeight: 800, cursor: 'pointer' }}
                  >
                    İptal Et
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── GÜNLÜK SEÇİM VE GELİŞMİŞ FİLTRELEME ÇUBUĞU ── */}
      <div className="mg-card">
        {/* Üst Satır: Gün Seçimi */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <div className="mg-date-bar">
            <span style={{ fontSize: '0.85rem', fontWeight: 900, color: '#2563EB' }}>📅 GÜNLÜK SEÇİM:</span>
            <div className="mg-date-controls">
              <button
                onClick={handlePrevDay}
                style={{ padding: '7px 12px', fontSize: '0.8rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FFFFFF', color: '#2563EB', fontWeight: 800, cursor: 'pointer' }}
              >
                ◀ Önceki
              </button>

              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="mg-input"
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid #2563EB',
                  background: '#FFFFFF',
                  color: '#0F172A',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                }}
              />

              <button
                onClick={handleNextDay}
                style={{ padding: '7px 12px', fontSize: '0.8rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FFFFFF', color: '#2563EB', fontWeight: 800, cursor: 'pointer' }}
              >
                Sonraki ▶
              </button>
            </div>

            <button
              onClick={handleToday}
              className="mg-today-btn"
              style={{
                padding: '7px 14px',
                fontSize: '0.8rem',
                borderRadius: 8,
                background: selectedDate === todayStr ? '#2563EB' : '#FFFFFF',
                border: '1px solid #2563EB',
                color: selectedDate === todayStr ? '#FFFFFF' : '#2563EB',
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              Bugün
            </button>
          </div>

          <div style={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 800 }}>
            Seçili Günün Toplam Mesajı: <strong style={{ color: '#2563EB' }}>{totalRecords} adet</strong>
          </div>
        </div>

        {/* Alt Satır: Filtreler (Hoca, İşlem, Durum, Arama) */}
        <div className="mg-filter-row" style={{ borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          {/* Arama Input */}
          <input
            type="text"
            placeholder="🔍 Alıcı adı, tel no veya mesaj içeriği..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mg-input"
            style={{ minWidth: 180, flex: 1 }}
          />

          {/* Hoca Filtresi */}
          <select
            value={filterSenderId}
            onChange={(e) => setFilterSenderId(e.target.value)}
            className="mg-select"
            style={{ color: '#2563EB', border: '1px solid #2563EB' }}
          >
            <option value="all">👨‍🏫 Tüm Hocalar / Gönderenler</option>
            {sendersList.map((s) => (
              <option key={s.sender_id || s.sender_name} value={s.sender_id || s.sender_name}>
                {s.sender_name} ({s.sender_role || 'ogretmen'})
              </option>
            ))}
          </select>

          {/* Modül / İşlem Filtresi */}
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
            className="mg-select"
            style={{ color: '#DC2626' }}
          >
            <option value="all">📌 Tüm İşlemler</option>
            <option value="yoklama">📋 Yoklama</option>
            <option value="rehberlik">🎯 Rehberlik</option>
            <option value="toplu_mesaj">📢 Toplu Mesaj</option>
            <option value="etut">📚 Etüt</option>
            <option value="deneme">📊 Deneme</option>
            <option value="odev">📝 Ödev</option>
            <option value="muhasebe">💰 Muhasebe</option>
            <option value="kullanici_hesap">🔑 Hesap Bilgileri</option>
          </select>

          {/* Durum Filtresi */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="mg-select"
            style={{ color: '#047857' }}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="sent">🟢 İletildi</option>
            <option value="failed">🔴 Başarısız</option>
            <option value="pending">🔵 Kuyrukta</option>
            <option value="cancelled">🚫 İptal Edildi</option>
          </select>
        </div>
      </div>

      {/* ── GÖNDERİM YAPAN HOCALAR VE İŞLEM BARLARI (DETAYLAR MODALDA AÇILIR) ── */}
      <div className="mg-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>📊</span>
            <span>
              {selectedDate === todayStr ? 'Bugün' : selectedDate} Gönderim Yapan Hocalar ve İşlem Barları ({groupedSummaryBars.length} Grup)
            </span>
          </div>
          <span style={{ fontSize: '0.78rem', color: '#2563EB', fontWeight: 700 }}>
            Kimlere ne mesaj gittiğini görmek için grubun üzerine veya "Mesajları Gör" butonuna tıklayınız
          </span>
        </div>

        {loadingLogs ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#2563EB', fontWeight: 800 }}>
            <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>🔄</div>
            <div>Veriler yükleniyor...</div>
          </div>
        ) : groupedSummaryBars.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#DC2626', fontWeight: 800 }}>
            <div style={{ fontSize: '2.2rem', marginBottom: 8 }}>📭</div>
            <div style={{ fontSize: '0.95rem' }}>Seçilen gün için gönderim kaydı bulunamadı.</div>
            <div style={{ fontSize: '0.8rem', color: '#64748B', marginTop: 4 }}>
              Farklı bir tarih seçebilir veya filtreleri temizleyebilirsiniz.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {groupedSummaryBars.map((group) => {
              const successPercent = group.total > 0 ? Math.round((group.sent / group.total) * 100) : 0;
              const failPercent = group.total > 0 ? Math.round((group.failed / group.total) * 100) : 0;

              return (
                <div
                  key={group.key}
                  className="mg-group-card"
                  onClick={() => {
                    setGroupSearchQuery('');
                    setGroupFilterStatus('all');
                    setSelectedGroup(group);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Üst Kısım: Hoca Adı, Modül Rozeti ve Sayımlar */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 900, fontSize: '0.95rem', color: '#0F172A' }}>
                        👨‍🏫 {group.sender_name}
                      </span>
                      {getModuleBadge(group.source_module)}
                      <span style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 700 }}>
                        (Son: {group.latestTime ? new Date(group.latestTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'})
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 900, color: '#047857' }}>
                        {group.sent} / {group.total} İletildi (%{successPercent})
                      </div>

                      {group.failed > 0 && (
                        <div style={{ fontSize: '0.82rem', fontWeight: 900, color: '#DC2626' }}>
                          ⚠️ {group.failed} Hata
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setGroupSearchQuery('');
                          setGroupFilterStatus('all');
                          setSelectedGroup(group);
                        }}
                        style={{
                          padding: '6px 14px',
                          fontSize: '0.78rem',
                          borderRadius: 8,
                          border: '1px solid #2563EB',
                          background: '#EFF6FF',
                          color: '#2563EB',
                          fontWeight: 900,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        Mesajları İncele ({group.total}) ➔
                      </button>
                    </div>
                  </div>

                  {/* İki Renkli Bölümlü İlerleme Çubuğu */}
                  <div
                    style={{
                      width: '100%',
                      height: 10,
                      background: '#E2E8F0',
                      borderRadius: 99,
                      overflow: 'hidden',
                      display: 'flex',
                    }}
                  >
                    <div
                      style={{
                        width: `${successPercent}%`,
                        height: '100%',
                        background: '#10B981',
                        transition: 'width 0.4s ease',
                      }}
                    />
                    <div
                      style={{
                        width: `${failPercent}%`,
                        height: '100%',
                        background: '#EF4444',
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── SEÇİLEN GRUBUN DETAYLI MESAJLARI MODALI (SLIDE-OVER DRAWER) ── */}
      {selectedGroup && (
        <div
          className="mg-modal-overlay"
          onClick={() => setSelectedGroup(null)}
        >
          <div
            className="mg-modal-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #E2E8F0',
                background: '#F8FAFC',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 900, color: '#0F172A' }}>
                    👨‍🏫 {selectedGroup.sender_name}
                  </h3>
                  {getModuleBadge(selectedGroup.source_module)}
                </div>

                {/* Özet Sayım Şeridi */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap', fontSize: '0.8rem', fontWeight: 800 }}>
                  <span style={{ color: '#0F172A' }}>
                    Tarih: <strong>{selectedDate}</strong>
                  </span>
                  <span style={{ color: '#2563EB' }}>
                    Toplam: <strong>{selectedGroup.total} Mesaj</strong>
                  </span>
                  <span style={{ color: '#047857' }}>
                    🟢 {selectedGroup.sent} Başarılı
                  </span>
                  {selectedGroup.failed > 0 && (
                    <span style={{ color: '#DC2626' }}>
                      🔴 {selectedGroup.failed} Hata
                    </span>
                  )}
                  {selectedGroup.pending > 0 && (
                    <span style={{ color: '#2563EB' }}>
                      🔵 {selectedGroup.pending} Kuyrukta
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setSelectedGroup(null)}
                style={{
                  background: '#FFFFFF',
                  border: '1px solid #CBD5E1',
                  color: '#0F172A',
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  fontSize: '1.1rem',
                  fontWeight: 900,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal İçi Hızlı Arama & Filtre Çubuğu */}
            <div
              style={{
                padding: '10px 16px',
                borderBottom: '1px solid #E2E8F0',
                background: '#FFFFFF',
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <input
                type="text"
                placeholder="🔍 Bu grupta alıcı, tel veya içerik ara..."
                value={groupSearchQuery}
                onChange={(e) => setGroupSearchQuery(e.target.value)}
                className="mg-input"
                style={{ flex: 1, minWidth: 150, padding: '6px 10px', fontSize: '0.8rem' }}
              />

              <select
                value={groupFilterStatus}
                onChange={(e) => setGroupFilterStatus(e.target.value)}
                className="mg-select"
                style={{ padding: '6px 10px', fontSize: '0.8rem' }}
              >
                <option value="all">Tüm Durumlar ({selectedGroup.total})</option>
                <option value="sent">🟢 İletilenler ({selectedGroup.sent})</option>
                <option value="failed">🔴 Hatalılar ({selectedGroup.failed})</option>
                <option value="pending">🔵 Kuyruktakiler ({selectedGroup.pending})</option>
                <option value="cancelled">🚫 İptal ({selectedGroup.cancelled})</option>
              </select>
            </div>

            {/* Modal Body: Detaylı Mesaj Listesi (Kime Ne Gitmiş) */}
            <div
              style={{
                flex: 1,
                padding: '14px 16px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                background: '#F8FAFC',
              }}
            >
              {modalFilteredItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#DC2626', fontWeight: 800 }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>🔍</div>
                  <div>Aramaya uygun mesaj kaydı bulunamadı.</div>
                </div>
              ) : (
                modalFilteredItems.map((item, idx) => {
                  const isSent = item.status === 'sent';
                  const isFailed = item.status === 'failed';

                  return (
                    <div
                      key={item.id || idx}
                      className="mg-detail-item"
                      style={{
                        background: isFailed ? '#FEF2F2' : isSent ? '#F0FDF4' : '#FFFFFF',
                        borderColor: '#E2E8F0',
                      }}
                    >
                      {/* Üst Satır: Alıcı Adı, Tel, Durum ve Saat */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 900, fontSize: '0.92rem', color: '#0F172A' }}>
                            👤 {item.recipient_name || '-'}
                          </span>
                          <span style={{ fontSize: '0.82rem', color: '#2563EB', fontWeight: 800 }}>
                            📱 {item.phone || item.to_phone || '-'}
                          </span>
                          {item.recipient_type && (
                            <span style={{ fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4, background: '#F1F5F9', color: '#475569', fontWeight: 800 }}>
                              {item.recipient_type === 'veli' ? 'Veli' : 'Öğrenci'}
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {getStatusBadge(item.status)}
                          <span style={{ fontSize: '0.76rem', color: '#0F172A', fontWeight: 800 }}>
                            {item.created_at ? new Date(item.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </span>
                        </div>
                      </div>

                      {/* WhatsApp Görünümlü Mesaj Balonu */}
                      <div
                        style={{
                          background: '#075E54',
                          borderRadius: 10,
                          padding: '8px 10px',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                      >
                        <div
                          style={{
                            background: '#128C7E',
                            borderRadius: 6,
                            padding: '8px 10px',
                            color: '#FFFFFF',
                            fontSize: '0.84rem',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                          }}
                        >
                          {item.message}
                        </div>
                      </div>

                      {/* Hata Mesajı & Tekrar Gönder Butonu (Varsa) */}
                      {isFailed && (
                        <div
                          style={{
                            background: '#FFFFFF',
                            border: '1px solid #FECACA',
                            borderRadius: 8,
                            padding: '8px 10px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 6,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 160, fontSize: '0.78rem', color: '#DC2626', fontWeight: 800 }}>
                            ⚠️ Hata: {item.error_message || 'İletim hatası oluştu'}
                          </div>

                          <button
                            onClick={() => handleRetryMessage(item.id)}
                            style={{
                              padding: '5px 12px',
                              borderRadius: 6,
                              background: '#10B981',
                              color: '#FFFFFF',
                              border: 'none',
                              fontSize: '0.75rem',
                              fontWeight: 900,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            🔄 Tekrar Gönder
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '12px 18px',
                borderTop: '1px solid #E2E8F0',
                background: '#FFFFFF',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '0.8rem', color: '#0F172A', fontWeight: 800 }}>
                Gösterilen: <strong>{modalFilteredItems.length}</strong> / Toplam <strong>{selectedGroup.total}</strong> Mesaj
              </span>

              <button
                onClick={() => setSelectedGroup(null)}
                style={{
                  padding: '8px 18px',
                  borderRadius: 8,
                  border: '1px solid #CBD5E1',
                  background: '#FFFFFF',
                  color: '#0F172A',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TÜM KUYRUĞU İPTAL ETME MODALI ── */}
      {showCancelAllModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowCancelAllModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: 16,
          }}
        >
          <div
            className="modal"
            style={{
              maxWidth: 400,
              width: '100%',
              background: '#FFFFFF',
              borderRadius: 14,
              padding: 22,
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              border: '1px solid #E2E8F0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>⚠️</div>
            <h3 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', fontWeight: 900, color: '#DC2626' }}>
              Tüm Kuyruğu İptal Et
            </h3>
            <p style={{ color: '#0F172A', fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.5, marginBottom: 16 }}>
              Kuyrukta bekleyen tüm ({queueState.queueLength}) adet mesajın gönderimi iptal edilecek. Onaylıyor musunuz?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowCancelAllModal(false)}
                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FFFFFF', color: '#0F172A', fontWeight: 800, cursor: 'pointer' }}
                disabled={actionLoading}
              >
                Vazgeç
              </button>
              <button
                onClick={handleCancelAll}
                disabled={actionLoading}
                style={{ background: '#EF4444', color: '#FFFFFF', border: 'none', padding: '7px 14px', borderRadius: 8, fontWeight: 900, cursor: 'pointer' }}
              >
                {actionLoading ? 'İptal Ediliyor...' : 'Evet, İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
